import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { titleize } from 'underscore.string';
import { useTranslation, Trans } from 'react-i18next';
import { massUpdateActions } from 'constants/MassUpdates';
import { getUpdateFieldForAction } from 'helpers/MassUpdates';
import LoadingCircle from 'components/ui/LoadingCircle';
import { showToast } from 'stores/ToastStore';
import OrdersGroup from './OrdersGroup';
import ProTip from 'components/ui/ProTip';
const PRO_TIP_MESSAGES = {
    [massUpdateActions.UPDATE_EMPTY_READY_FOR_PICKUP]: 'ENSURE_INFO_FILLED_BEFORE_APPLY_ALL_DOMESTIC_EXPORT_PICKUP_EXCLUDED',
};
function MassUpdateModal({ show, orders: ordersIn = [], onHide, action, }) {
    const { t } = useTranslation();
    const [proTipVisible, setProTipVisible] = useState(true);
    const [loading /*, setLoading*/] = useState(false);
    const [saving, setSaving] = useState(false);
    const [orders, setOrders] = useState(ordersIn);
    const [updates, setUpdates] = useState({});
    const [invalidFields, setInvalidFields] = useState(new Set());
    useEffect(() => {
        setOrders(ordersIn);
    }, [ordersIn]);
    const fieldToUpdate = useMemo(() => getUpdateFieldForAction(action), [action]);
    const validateField = useCallback(() => {
        // this can be extended for validation of other fields
        const orderIdsWithInvalidField = new Set(orders
            .filter((order) => !order[fieldToUpdate])
            .map((order) => order.order_oid));
        // if there is an update with a valid value, remove the id from the array
        const keys = Object.keys(updates);
        keys.forEach((orderId) => {
            var _a, _b;
            if ((_a = updates[orderId]) === null || _a === void 0 ? void 0 : _a[fieldToUpdate]) {
                orderIdsWithInvalidField.delete(orderId);
            }
            else if (((_b = updates[orderId]) === null || _b === void 0 ? void 0 : _b[fieldToUpdate]) === null) {
                orderIdsWithInvalidField.add(orderId);
            }
        });
        setInvalidFields(() => {
            return new Set(orderIdsWithInvalidField);
        });
    }, [orders, fieldToUpdate, updates]);
    useEffect(() => {
        validateField();
    }, [validateField]);
    const handleHide = useCallback(() => {
        onHide();
    }, [onHide]);
    const getTitle = useCallback(() => {
        return t('UPDATE') + ' ' + t('ACCOUNTING_REVIEW_DATE');
    }, [t]);
    const handleCloseTip = useCallback((e) => {
        if (e)
            e.preventDefault();
        setProTipVisible(false);
    }, []);
    const bulkSaveAccountReviewDates = useCallback(async () => {
        const orderIds = Object.keys(updates);
        if (!orderIds.length) {
            return;
        }
        const operations = orderIds.map((orderId) => ({
            filter: { _id: orderId },
            update: {
                accounting_review_date: updates[orderId].accounting_review_date,
            },
        }));
        const updatesToSend = {
            operations,
        };
        setSaving(true);
        try {
            // mock data here, comment out for now
            //await bulkUpdateOrderAccountReviewDate(updatesToSend);
            await new Promise((resolve) => setTimeout(resolve, 650));
            showToast(t('UPDATE_ACCOUNTING_REVIEW_DATE_SUCCESS'), t('UPDATE_ACCOUNTING_REVIEW_DATE_SUCCESS'), 'success');
            setSaving(false);
            onHide();
        }
        catch (error) {
            showToast(t('FAILED_UPDATE_ACCOUNTING_REVIEW_DATE'), error.message, 'error');
            setSaving(false);
        }
    }, [updates, t, onHide]);
    const handleClearAll = useCallback(() => {
        const orderIds = orders.map((order) => order.order_oid);
        setUpdates((currentUpdates) => {
            const updated = Object.assign({}, currentUpdates);
            orderIds.forEach((oid) => {
                var _a;
                updated[oid] = (_a = updated[oid]) !== null && _a !== void 0 ? _a : {};
                updated[oid] = Object.assign(Object.assign({}, updated[oid]), { [fieldToUpdate]: null });
            });
            return updated;
        });
    }, [orders, fieldToUpdate]);
    const handleRemoveOrder = useCallback(({ order_oid }) => {
        setOrders((oldOrders) => oldOrders.filter((order) => order.order_oid !== order_oid));
        // also remove any validation issues
        setInvalidFields((currentInvalid) => {
            const next = new Set(currentInvalid);
            next.delete(order_oid);
            return next;
        });
        setUpdates((currentUpdates) => {
            const updated = Object.assign({}, currentUpdates);
            delete updated[order_oid];
            return updated;
        });
    }, []);
    const handleFieldChange = useCallback((orderId, field, value) => {
        setUpdates((currentUpdates) => {
            var _a;
            const updated = Object.assign({}, currentUpdates);
            updated[orderId] = (_a = updated[orderId]) !== null && _a !== void 0 ? _a : {};
            updated[orderId] = Object.assign(Object.assign({}, updated[orderId]), { [field]: value });
            return updated;
        });
    }, []);
    const applyFieldToAll = useCallback((orderId) => {
        // order_oid
        const orderIds = orders
            .map((order) => order.order_oid)
            .filter((oid) => oid !== orderId);
        if (!orderIds.length) {
            return;
        }
        const origOrder = orders.find((order) => order.order_oid === orderId);
        setUpdates((currentUpdates) => {
            var _a;
            // use latest update for this field or fall back on the current value
            const currValue = ((_a = currentUpdates[orderId]) === null || _a === void 0 ? void 0 : _a[fieldToUpdate]) || origOrder[fieldToUpdate];
            const updated = Object.assign({}, currentUpdates);
            orderIds.forEach((oid) => {
                var _a;
                updated[oid] = (_a = updated[oid]) !== null && _a !== void 0 ? _a : {};
                updated[oid] = Object.assign(Object.assign({}, updated[oid]), { [fieldToUpdate]: currValue });
            });
            return updated;
        });
    }, [orders, fieldToUpdate]);
    const getLocalizationSlug = useCallback((slugAction) => {
        return Object.keys(massUpdateActions).find((key) => massUpdateActions[key] === slugAction);
    }, []);
    const getModalDetails = useCallback((detailAction) => {
        const localizationSlug = getLocalizationSlug(detailAction);
        switch (detailAction) {
            case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
                return {
                    modalTitle: titleize(t(localizationSlug)),
                    applyToAll: applyFieldToAll,
                    onSaveAll: bulkSaveAccountReviewDates,
                };
            default:
                return null;
        }
    }, [getLocalizationSlug, t, applyFieldToAll, bulkSaveAccountReviewDates]);
    const renderProTip = () => {
        if (!proTipVisible) {
            return null;
        }
        const message = PRO_TIP_MESSAGES[action] || 'ENSURE_INFO_FILLED_BEFORE_APPLY_ALL';
        return (React.createElement(ProTip, { onClose: handleCloseTip },
            React.createElement(Trans, { i18nKey: message })));
    };
    const renderLoading = () => {
        return React.createElement(LoadingCircle, { text: `${t('LOADING')}` });
    };
    const renderOrders = () => {
        const modalDetails = getModalDetails(action);
        if (!modalDetails)
            return null;
        const { modalTitle, applyToAll, onSaveAll } = modalDetails;
        if (orders.length === 0) {
            return (React.createElement("div", { className: "shadow p-4 bg-white flex justify-center" },
                React.createElement("div", { className: "flex justify-between" },
                    React.createElement("h4", { className: "mb-2 text-grey-darker" }, t('NO_VALID_ORDERS_TO_UPDATE')))));
        }
        return (React.createElement(OrdersGroup, { key: action, updateAction: action, title: modalTitle, orders: orders, saving: saving, onRemoveOrder: handleRemoveOrder, onFieldChange: handleFieldChange, onApplyToAll: applyToAll, onClearAll: handleClearAll, onSaveAll: onSaveAll, updates: updates, action: action, invalidFields: invalidFields }));
    };
    const modalBody = renderOrders();
    return (React.createElement(Modal, { show: show, onHide: handleHide, backdrop: "static", dialogClassName: "modal-override-large bg-grey-modal", keyboard: false, enforceFocus: false },
        React.createElement(Modal.Header, { closeButton: true },
            React.createElement(Modal.Title, null, getTitle())),
        React.createElement(Modal.Body, null,
            React.createElement("div", { className: "bg-grey-lightest" },
                renderProTip(),
                React.createElement("div", { className: "mt-4" }, loading ? renderLoading() : modalBody))),
        React.createElement(Modal.Footer, null,
            React.createElement("div", { className: "flex" },
                React.createElement("div", { className: "w-3/4" }, "\u00A0"),
                React.createElement("div", { className: "w-1/4 mt-3" },
                    React.createElement("div", { className: "flex justify-end button-group" },
                        React.createElement("button", { className: "block button secondary-action", onClick: () => handleHide() }, titleize(t('CLOSE')))))))));
}
export default MassUpdateModal;
